import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from '../contexts/AuthContext';
import { COLORS } from '../styles/theme';
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  Pin,
  InfoWindow,
  useMap,
} from '@vis.gl/react-google-maps';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GOOGLE_MAPS_API_KEY = 'AIzaSyBNpVf2wxLdPMDj6_VAKG_KM179d7sV2vI';
const PROPERTY_CENTER = { lat: 44.866701, lng: -77.381202 };

const POI_COLORS: Record<string, string> = {
  stand: '#28A745', blind: '#1B3B1B', camera: '#6F42C1', feeding: '#FF6B35',
  sighting: '#E83E8C', dock: '#17A2B8', other: '#888',
};

const AREA_COLORS: Record<string, string> = {
  hunting: 'rgba(40,167,69,0.3)', fishing: 'rgba(0,123,255,0.3)',
  restricted: 'rgba(220,53,69,0.3)', other: 'rgba(136,136,136,0.3)',
};
const AREA_STROKE: Record<string, string> = {
  hunting: '#28A745', fishing: '#007BFF', restricted: '#DC3545', other: '#888',
};

const TRAIL_COLORS: Record<string, string> = {
  easy: '#28A745', moderate: '#FFC107', hard: '#DC3545',
};

const EQUIP_COLORS: Record<string, string> = {
  hunting: '#28A745', fishing: '#007BFF', boating: '#17A2B8', atving: '#FF6B35',
  camping: '#FFC107', bushcraft: '#6F42C1', biking: '#E83E8C',
  hiking_geocaching: '#20C997', other: '#888',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Camp {
  id: string; name: string; locationLat: string | null; locationLon: string | null;
  pinColor: string | null; condition: string; cabinCount: number;
}
interface EquipItem {
  id: string; name: string; category: string; itemType: string | null;
  condition: string; locationLat: string | null; locationLon: string | null;
}
interface POI {
  id: string; name: string; type: string;
  locationLat: string; locationLon: string;
}
interface Trail {
  id: string; name: string; surface: string; difficulty: string;
  path: Array<{ lat: number; lon: number }>;
}
interface PropertyItem {
  id: string; name: string; strokeColor: string | null;
  boundary: Array<{ lat: number; lon: number }>;
}
interface Area {
  id: string; name: string; category: string;
  boundary: Array<{ lat: number; lon: number }>;
}

interface LayerVisibility {
  properties: boolean;
  camps: boolean;
  equipment: boolean;
  pois: boolean;
  trails: boolean;
  areas: boolean;
}

// ---------------------------------------------------------------------------
// Overlay renderer (trails + areas via Maps JS API)
// ---------------------------------------------------------------------------

function MapOverlays({ properties, trails, areas, visible }: {
  properties: PropertyItem[]; trails: Trail[]; areas: Area[]; visible: LayerVisibility;
}) {
  const map = useMap();
  const overlaysRef = useRef<{ polylines: google.maps.Polyline[]; polygons: google.maps.Polygon[] }>({ polylines: [], polygons: [] });

  useEffect(() => {
    if (!map) return;

    // Clear old overlays
    overlaysRef.current.polylines.forEach((p) => p.setMap(null));
    overlaysRef.current.polygons.forEach((p) => p.setMap(null));
    overlaysRef.current = { polylines: [], polygons: [] };

    // Draw property boundaries (lines only, no fill)
    if (visible.properties) {
      for (const prop of properties) {
        if (!prop.boundary || prop.boundary.length < 2) continue;
        const path = [...prop.boundary.map((p) => ({ lat: p.lat, lng: p.lon }))];
        if (path.length >= 3) path.push(path[0]);
        const polyline = new google.maps.Polyline({
          path,
          strokeColor: prop.strokeColor || '#FFD700',
          strokeWeight: 2,
          strokeOpacity: 0.8,
          map,
        });
        overlaysRef.current.polylines.push(polyline);
      }
    }

    // Draw trails
    if (visible.trails) {
      for (const trail of trails) {
        if (!trail.path || trail.path.length < 2) continue;
        const polyline = new google.maps.Polyline({
          path: trail.path.map((p) => ({ lat: p.lat, lng: p.lon })),
          strokeColor: TRAIL_COLORS[trail.difficulty] || '#FFC107',
          strokeWeight: 3,
          strokeOpacity: 0.9,
          map,
        });
        overlaysRef.current.polylines.push(polyline);
      }
    }

    // Draw areas
    if (visible.areas) {
      for (const area of areas) {
        if (!area.boundary || area.boundary.length < 3) continue;
        const polygon = new google.maps.Polygon({
          paths: area.boundary.map((p) => ({ lat: p.lat, lng: p.lon })),
          fillColor: AREA_COLORS[area.category] || 'rgba(136,136,136,0.3)',
          fillOpacity: 0.4,
          strokeColor: AREA_STROKE[area.category] || '#888',
          strokeWeight: 2,
          map,
        });
        overlaysRef.current.polygons.push(polygon);
      }
    }

    return () => {
      overlaysRef.current.polylines.forEach((p) => p.setMap(null));
      overlaysRef.current.polygons.forEach((p) => p.setMap(null));
    };
  }, [map, properties, trails, areas, visible.properties, visible.trails, visible.areas]);

  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MapPage() {
  const [propertyItems, setPropertyItems] = useState<PropertyItem[]>([]);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [equipment, setEquipment] = useState<EquipItem[]>([]);
  const [pois, setPois] = useState<POI[]>([]);
  const [trails, setTrails] = useState<Trail[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [infoId, setInfoId] = useState<string | null>(null);
  const [infoType, setInfoType] = useState<string>('');

  const [visible, setVisible] = useState<LayerVisibility>({
    properties: true, camps: true, equipment: true, pois: true, trails: true, areas: true,
  });

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    setLoading(true);
    const [propsRes, campsRes, equipRes, poisRes, trailsRes, areasRes] = await Promise.all([
      apiFetch<PropertyItem[]>('/api/admin/properties'),
      apiFetch<Camp[]>('/api/admin/camps'),
      apiFetch<EquipItem[]>('/api/admin/equipment'),
      apiFetch<POI[]>('/api/admin/map/pois'),
      apiFetch<Trail[]>('/api/admin/map/trails'),
      apiFetch<Area[]>('/api/admin/map/areas'),
    ]);
    if (propsRes.success && propsRes.data) setPropertyItems(propsRes.data);
    if (campsRes.success && campsRes.data) setCamps(campsRes.data);
    if (equipRes.success && equipRes.data) setEquipment(equipRes.data);
    if (poisRes.success && poisRes.data) setPois(poisRes.data);
    if (trailsRes.success && trailsRes.data) setTrails(trailsRes.data);
    if (areasRes.success && areasRes.data) setAreas(areasRes.data);
    setLoading(false);
  }

  const toggleLayer = useCallback((layer: keyof LayerVisibility) => {
    setVisible((v) => ({ ...v, [layer]: !v[layer] }));
  }, []);

  const campMarkers = visible.camps ? camps.filter((c) => c.locationLat && c.locationLon) : [];
  const equipMarkers = visible.equipment ? equipment.filter((e) => e.locationLat && e.locationLon) : [];
  const poiMarkers = visible.pois ? pois.filter((p) => p.locationLat && p.locationLon) : [];

  function showInfo(id: string, type: string) {
    setInfoId(id === infoId && type === infoType ? null : id);
    setInfoType(type);
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: COLORS.textMuted }}>Loading map data...</div>;

  return (
    <div style={s.page}>
      {/* LEFT: Full map */}
      <div style={s.mapCol}>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            defaultCenter={PROPERTY_CENTER}
            defaultZoom={15}
            mapTypeId="satellite"
            gestureHandling="greedy"
            fullscreenControl={false}
            mapId="grimsthorpe-admin-fullmap"
            style={{ width: '100%', height: '100%' }}
          >
            {/* Camp markers */}
            {campMarkers.map((camp) => (
              <AdvancedMarker
                key={`camp-${camp.id}`}
                position={{ lat: parseFloat(camp.locationLat!), lng: parseFloat(camp.locationLon!) }}
                title={camp.name}
                onClick={() => showInfo(camp.id, 'camp')}
              >
                <Pin background={camp.pinColor || '#1B3B1B'} borderColor="#000" glyphColor="#FFF" />
              </AdvancedMarker>
            ))}

            {/* Equipment markers */}
            {equipMarkers.map((eq) => (
              <AdvancedMarker
                key={`equip-${eq.id}`}
                position={{ lat: parseFloat(eq.locationLat!), lng: parseFloat(eq.locationLon!) }}
                title={eq.name}
                onClick={() => showInfo(eq.id, 'equip')}
              >
                <Pin
                  background={EQUIP_COLORS[eq.category] || '#888'}
                  borderColor="#000"
                  glyphColor="#FFF"
                  scale={0.8}
                />
              </AdvancedMarker>
            ))}

            {/* POI markers */}
            {poiMarkers.map((poi) => (
              <AdvancedMarker
                key={`poi-${poi.id}`}
                position={{ lat: parseFloat(poi.locationLat), lng: parseFloat(poi.locationLon) }}
                title={poi.name}
                onClick={() => showInfo(poi.id, 'poi')}
              >
                <Pin
                  background={POI_COLORS[poi.type] || '#888'}
                  borderColor="#333"
                  glyphColor="#FFF"
                  scale={0.7}
                />
              </AdvancedMarker>
            ))}

            {/* Trail start-point markers */}
            {visible.trails && trails.filter((t) => t.path && t.path.length > 0).map((trail) => (
              <AdvancedMarker
                key={`trail-${trail.id}`}
                position={{ lat: trail.path[0].lat, lng: trail.path[0].lon }}
                title={trail.name}
                onClick={() => showInfo(trail.id, 'trail')}
              >
                <Pin
                  background={TRAIL_COLORS[trail.difficulty] || '#FFC107'}
                  borderColor="#000"
                  glyphColor="#FFF"
                  scale={0.7}
                />
              </AdvancedMarker>
            ))}

            {/* Area first-point markers */}
            {visible.areas && areas.filter((a) => a.boundary && a.boundary.length > 0).map((area) => (
              <AdvancedMarker
                key={`area-${area.id}`}
                position={{ lat: area.boundary[0].lat, lng: area.boundary[0].lon }}
                title={area.name}
                onClick={() => showInfo(area.id, 'area')}
              >
                <Pin
                  background={AREA_STROKE[area.category] || '#888'}
                  borderColor="#000"
                  glyphColor="#FFF"
                  scale={0.6}
                />
              </AdvancedMarker>
            ))}

            {/* Info windows */}
            {infoId && infoType === 'camp' && (() => {
              const c = camps.find((x) => x.id === infoId);
              if (!c?.locationLat) return null;
              return (
                <InfoWindow position={{ lat: parseFloat(c.locationLat!), lng: parseFloat(c.locationLon!) }} onCloseClick={() => setInfoId(null)} pixelOffset={[0, -40]}>
                  <div style={{ color: '#000', minWidth: 160 }}>
                    <strong>{c.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>Base Camp &middot; {c.condition?.replace('_', ' ')} &middot; {c.cabinCount} building{c.cabinCount !== 1 ? 's' : ''}</p>
                  </div>
                </InfoWindow>
              );
            })()}
            {infoId && infoType === 'equip' && (() => {
              const e = equipment.find((x) => x.id === infoId);
              if (!e?.locationLat) return null;
              return (
                <InfoWindow position={{ lat: parseFloat(e.locationLat!), lng: parseFloat(e.locationLon!) }} onCloseClick={() => setInfoId(null)} pixelOffset={[0, -35]}>
                  <div style={{ color: '#000', minWidth: 140 }}>
                    <strong>{e.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>{e.category} &middot; {e.condition?.replace('_', ' ')}</p>
                  </div>
                </InfoWindow>
              );
            })()}
            {infoId && infoType === 'poi' && (() => {
              const p = pois.find((x) => x.id === infoId);
              if (!p) return null;
              return (
                <InfoWindow position={{ lat: parseFloat(p.locationLat), lng: parseFloat(p.locationLon) }} onCloseClick={() => setInfoId(null)} pixelOffset={[0, -30]}>
                  <div style={{ color: '#000', minWidth: 120 }}>
                    <strong>{p.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>{p.type}</p>
                  </div>
                </InfoWindow>
              );
            })()}
            {infoId && infoType === 'trail' && (() => {
              const t = trails.find((x) => x.id === infoId);
              if (!t?.path?.length) return null;
              return (
                <InfoWindow position={{ lat: t.path[0].lat, lng: t.path[0].lon }} onCloseClick={() => setInfoId(null)} pixelOffset={[0, -30]}>
                  <div style={{ color: '#000', minWidth: 140 }}>
                    <strong>{t.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>{t.difficulty} &middot; {t.surface} &middot; {t.path.length} pts</p>
                  </div>
                </InfoWindow>
              );
            })()}
            {infoId && infoType === 'area' && (() => {
              const a = areas.find((x) => x.id === infoId);
              if (!a?.boundary?.length) return null;
              return (
                <InfoWindow position={{ lat: a.boundary[0].lat, lng: a.boundary[0].lon }} onCloseClick={() => setInfoId(null)} pixelOffset={[0, -30]}>
                  <div style={{ color: '#000', minWidth: 120 }}>
                    <strong>{a.name}</strong>
                    <p style={{ margin: '4px 0', fontSize: 11 }}>{a.category} &middot; {a.boundary.length} points</p>
                  </div>
                </InfoWindow>
              );
            })()}

            <MapOverlays properties={propertyItems} trails={trails} areas={areas} visible={visible} />
          </GoogleMap>
        </APIProvider>
      </div>

      {/* RIGHT: Layer controls + item lists */}
      <div style={s.panelCol}>
        <div style={s.panelContent}>
          <h3 style={s.panelTitle}>Map Layers</h3>

          {/* Layer toggles */}
          <LayerToggle label="Properties" count={propertyItems.length} color="#FFD700" active={visible.properties} onToggle={() => toggleLayer('properties')} />
          <LayerToggle label="Base Camps" count={camps.filter((c) => c.locationLat).length} color="#1B3B1B" active={visible.camps} onToggle={() => toggleLayer('camps')} />
          <LayerToggle label="Equipment" count={equipment.filter((e) => e.locationLat).length} color="#FF6B35" active={visible.equipment} onToggle={() => toggleLayer('equipment')} />
          <LayerToggle label="Points of Interest" count={pois.length} color="#6F42C1" active={visible.pois} onToggle={() => toggleLayer('pois')} />
          <LayerToggle label="Trails" count={trails.length} color="#FFC107" active={visible.trails} onToggle={() => toggleLayer('trails')} />
          <LayerToggle label="Areas" count={areas.length} color="#28A745" active={visible.areas} onToggle={() => toggleLayer('areas')} />

          <div style={{ borderTop: `1px solid ${COLORS.borderDefault}`, marginTop: 16, paddingTop: 16 }}>
            {/* Property items */}
            {visible.properties && propertyItems.length > 0 && (
              <ItemSection title="Properties">
                {propertyItems.map((p) => (
                  <ItemRow key={p.id} label={p.name} sub={`${p.boundary?.length || 0} boundary points`}
                    dotColor={p.strokeColor || '#FFD700'}
                  />
                ))}
              </ItemSection>
            )}

            {/* Camp items */}
            {visible.camps && campMarkers.length > 0 && (
              <ItemSection title="Base Camps">
                {campMarkers.map((c) => (
                  <ItemRow key={c.id} label={c.name} sub={`${c.cabinCount} bldg · ${c.condition?.replace('_', ' ')}`}
                    dotColor={c.pinColor || '#1B3B1B'}
                    onClick={() => showInfo(c.id, 'camp')}
                    active={infoId === c.id}
                  />
                ))}
              </ItemSection>
            )}

            {/* Equipment items */}
            {visible.equipment && equipMarkers.length > 0 && (
              <ItemSection title="Equipment">
                {equipMarkers.map((e) => (
                  <ItemRow key={e.id} label={e.name} sub={`${e.category} · ${e.condition?.replace('_', ' ')}`}
                    dotColor={EQUIP_COLORS[e.category] || '#888'}
                    onClick={() => showInfo(e.id, 'equip')}
                    active={infoId === e.id}
                  />
                ))}
              </ItemSection>
            )}

            {/* POI items */}
            {visible.pois && poiMarkers.length > 0 && (
              <ItemSection title="Points of Interest">
                {poiMarkers.map((p) => (
                  <ItemRow key={p.id} label={p.name} sub={p.type}
                    dotColor={POI_COLORS[p.type] || '#888'}
                    onClick={() => showInfo(p.id, 'poi')}
                    active={infoId === p.id}
                  />
                ))}
              </ItemSection>
            )}

            {/* Trail items */}
            {visible.trails && trails.length > 0 && (
              <ItemSection title="Trails">
                {trails.map((t) => (
                  <ItemRow key={t.id} label={t.name} sub={`${t.surface} · ${t.difficulty}`}
                    dotColor={TRAIL_COLORS[t.difficulty] || '#FFC107'}
                  />
                ))}
              </ItemSection>
            )}

            {/* Area items */}
            {visible.areas && areas.length > 0 && (
              <ItemSection title="Areas">
                {areas.map((a) => (
                  <ItemRow key={a.id} label={a.name} sub={a.category}
                    dotColor={AREA_STROKE[a.category] || '#888'}
                  />
                ))}
              </ItemSection>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LayerToggle({ label, count, color, active, onToggle }: {
  label: string; count: number; color: string; active: boolean; onToggle: () => void;
}) {
  return (
    <div style={s.layerRow} onClick={onToggle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 14, height: 14, borderRadius: 3, backgroundColor: active ? color : 'transparent',
          border: `2px solid ${active ? color : COLORS.borderDefault}`,
        }} />
        <span style={{ color: active ? COLORS.textPrimary : COLORS.textMuted, fontSize: 13, fontWeight: 500 }}>{label}</span>
      </div>
      <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{count}</span>
    </div>
  );
}

function ItemSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function ItemRow({ label, sub, dotColor, onClick, active }: {
  label: string; sub: string; dotColor: string; onClick?: () => void; active?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', marginBottom: 2,
        borderRadius: 4, cursor: onClick ? 'pointer' : 'default',
        backgroundColor: active ? 'rgba(184,151,94,0.1)' : 'transparent',
      }}
      onClick={onClick}
    >
      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ color: COLORS.textPrimary, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
        <div style={{ color: COLORS.textMuted, fontSize: 10 }}>{sub}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', height: 'calc(100vh - 60px)', gap: 0 },
  mapCol: { flex: 1, minWidth: 0 },
  panelCol: { width: 320, flexShrink: 0, overflowY: 'auto', borderLeft: `1px solid ${COLORS.borderDefault}`, backgroundColor: COLORS.backgroundSecondary },
  panelContent: { padding: 16 },
  panelTitle: { fontSize: 18, fontWeight: 600, color: COLORS.textPrimary, margin: '0 0 14px' },
  layerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px', cursor: 'pointer', borderBottom: `1px solid ${COLORS.borderDefault}` },
};
